import {
  $framework,
  createInternalComponent,
  useFeedback,
  useTranslation,
} from "@opendash/core";
import { Icon } from "@opendash/icons";
import {
  DataItemInterface,
  useDataItem,
  useDataService,
} from "@opendash/plugin-timeseries";
import { Button, Divider, Empty, List, Modal } from "antd";
import * as React from "react";
import { AlarmCreate, AlarmInterface, useMonitoringService } from "..";
import { AlarmEditModal } from "./AlarmEditModal";

interface ListProps {
  alarms: AlarmInterface[];
  itemForAlarm?: DataItemInterface;
  dimension?: number;
  onSave?: (id: string) => void;
}

interface ItemProps {
  alarm: AlarmInterface;
  onEdit: (alarm: AlarmInterface) => void; // Neue Prop hinzufügen
}

const AlarmListItem = createInternalComponent<ItemProps>(
  function AlarmListItem({ alarm, onEdit }) {
    const t = useTranslation();
    const DataService = useDataService();
    const MonitoringService = useMonitoringService();
    const { message } = useFeedback();

    const item = useDataItem(alarm.item[0], alarm.item[1]);
    const title = DataService.getItemName(item, alarm.item[2]);
    let description = t("opendash:data.trigger." + alarm.trigger.type);

    // Beschreibung aufbauen
    if ("string" in alarm.trigger) {
      description += ` '${alarm.trigger.string}'`;
    }
    if ("value" in alarm.trigger) {
      description += ` ${alarm.trigger.value}`;
    }
    if ("min" in alarm.trigger && "max" in alarm.trigger) {
      description += ` ${alarm.trigger.min} - ${alarm.trigger.max}`;
    }
    if ("time" in alarm.trigger) {
      description += ` ${alarm.trigger.time.value / 1000 / 60} Minuten`;
    }
    if (alarm.action?.label) {
      description += ` - ${alarm.action?.label}`;
      if (alarm.action?.target) {
        description += ` ${alarm.action?.target}`;
      }
    }

    function editAlarm() {
      onEdit(alarm); // Rufe den Callback mit dem aktuellen Alarm auf
    }

    function deleteAlarm() {
      Modal.confirm({
        title: t("opendash:monitoring.alarms.delete.confirm_title"),
        content: t("opendash:monitoring.alarms.delete.confirm_message"),
        okText: t("opendash:monitoring.alarms.delete.confirm_ok"),
        okType: "danger",
        onOk: () => {
          MonitoringService.deleteAlarm(alarm).then(
            () => {
              message.success(t("opendash:monitoring.alarms.delete.success"));
            },
            () => {
              message.error(t("opendash:monitoring.alarms.delete.error"));
            }
          );
        },
      });
    }

    return (
      <List.Item
        actions={[
          <Button
            type="link"
            icon={
              <Icon
                icon="fa:pen"
                style={{ color: $framework.ui2.primaryColor }}
              />
            }
            onClick={editAlarm}
            aria-label="Edit alarm"
          />,
          <Button
            type="link"
            icon={<Icon icon="fa:trash" style={{ color: "#ff4d4d" }} />}
            onClick={deleteAlarm}
            aria-label="Delete alarm"
          />,
        ]}
      >
        <List.Item.Meta title={title} description={description} />
      </List.Item>
    );
  }
);

export const AlarmList = createInternalComponent<ListProps>(function AlarmList({
  alarms,
  itemForAlarm,
  dimension = 0,
}) {
  const t = useTranslation();
  const [addMode, setAddMode] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [alarmToEdit, setAlarmToEdit] = React.useState<AlarmInterface | null>(
    null
  );

  const handleEditAlarm = (alarm: AlarmInterface) => {
    setAlarmToEdit(alarm);
    setEditMode(true);
    setAddMode(false);
  };

  return (
    <>
      <List
        header={
          itemForAlarm && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0 }}>
                {t("opendash:monitoring.alarms.title")}
              </h3>
              <Button
                type="primary"
                icon={<Icon icon="fa:plus" />}
                onClick={() => {
                  setAddMode(!addMode);
                  setEditMode(false);
                }}
              >
                {t("opendash:monitoring.alarms.add")}
              </Button>
            </div>
          )
        }
        dataSource={alarms}
        locale={{
          emptyText: (
            <Empty
              description={<span>{t("opendash:monitoring.alarms.empty")}</span>}
            />
          ),
        }}
        renderItem={(alarm) => (
          <AlarmListItem alarm={alarm} onEdit={handleEditAlarm} />
        )}
      />
      {(editMode || addMode) && <Divider />}
      {itemForAlarm && addMode && !editMode && (
        <>
          <Button
            onClick={() => setAddMode(false)}
            style={{
              width: "100%", // Volle Breite
              border: "1px dashed " + $framework.ui2.primaryColor, // Gestrichelte Border
              backgroundColor: "transparent", // Transparenten Hintergrund
              color: $framework.ui2.primaryColor, // Farbe des Textes
              marginBottom: 16, // Abstand nach unten
              textAlign: "center", // Text zentrieren
            }}
          >
            {t("opendash:ui.back")}
          </Button>
          <AlarmCreate
            item={itemForAlarm}
            dimension={dimension}
            onSave={() => setAddMode(false)}
          />
        </>
      )}

      {editMode && alarmToEdit && (
        <>
          <Button
            onClick={() => setEditMode(false)}
            style={{
              width: "100%", // Volle Breite
              border: "1px dashed " + $framework.ui2.primaryColor, // Gestrichelte Border
              backgroundColor: "transparent", // Transparenten Hintergrund
              color: $framework.ui2.primaryColor, // Farbe des Textes
              marginBottom: 16, // Abstand nach unten
              textAlign: "center", // Text zentrieren
            }}
          >
            {t("opendash:ui.back")}
          </Button>

          <AlarmEditModal
            alarm={alarmToEdit}
            onClose={() => {
              setEditMode(false);
              setAlarmToEdit(null);
            }}
          />
        </>
      )}
    </>
  );
});
